<template>
  <div
    class="min-h-screen bg-[radial-gradient(circle_at_top,_#eff6ff_0%,_#f8fafc_38%,_#e2e8f0_100%)] px-4 py-6 md:px-8 md:py-10">
    <div
      class="mx-auto max-w-5xl overflow-hidden rounded-[28px] border border-white/60 bg-white/95 shadow-2xl shadow-slate-300/50 backdrop-blur">
      <section
        class="relative overflow-hidden border-b border-slate-200 bg-slate-950 px-6 py-8 text-white md:px-10 md:py-10">
        <div
          class="absolute inset-0 bg-[linear-gradient(120deg,rgba(37,99,235,0.18),transparent_38%,rgba(14,165,233,0.15))]">
        </div>
        <div class="relative flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div>
            <p class="text-xs uppercase tracking-[0.35em] text-slate-300">Seguimiento público</p>
            <h1 class="mt-3 text-3xl font-semibold md:text-5xl">{{ company.brandName || 'Estado de tu equipo' }}</h1>
            <p class="mt-3 max-w-2xl text-sm text-slate-300 md:text-base">
              Consulta el avance de tu recepción sin iniciar sesión. Esta vista usa una carga puntual y actualización
              manual para no degradar el sistema con consultas en segundo plano.
            </p>
          </div>
          <div class="rounded-2xl border border-white/10 bg-white/5 px-5 py-4 text-sm text-slate-200">
            <div class="flex items-start justify-between gap-4">
              <div>
                <p class="text-xs uppercase tracking-[0.24em] text-slate-400">Negocio</p>
                <p class="mt-2 text-lg font-semibold text-white">{{ company.brandName || 'Taller' }}</p>
                <p class="mt-2 text-xs text-slate-300">{{ company.phone || 'Teléfono no disponible' }}</p>
                <p v-if="lastLoadedAt" class="mt-2 text-[11px] text-slate-400">Última consulta: {{
                  formatDate(lastLoadedAt) }}</p>
              </div>
              <button type="button"
                class="rounded-lg border border-white/15 bg-white/10 px-3 py-2 text-xs font-semibold uppercase tracking-[0.15em] text-white transition hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-60"
                :disabled="loading || refreshing" @click="loadTracking()">
                {{ refreshing ? 'Actualizando...' : 'Actualizar' }}
              </button>
            </div>
          </div>
        </div>
      </section>

      <section class="px-6 py-8 md:px-10">
        <div v-if="loading" class="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-sm text-slate-600">
          Cargando seguimiento del equipo...
        </div>

        <div v-else-if="errorMessage" class="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700">
          {{ errorMessage }}
        </div>

        <div v-else-if="tracking" class="space-y-6">
          <div class="grid gap-4 md:grid-cols-3">
            <article class="rounded-2xl border border-slate-200 bg-slate-50/70 p-5">
              <p class="text-xs uppercase tracking-[0.2em] text-slate-500">Orden</p>
              <p class="mt-2 text-xl font-semibold text-slate-800">{{ tracking.codigo || 'Pendiente' }}</p>
            </article>

            <article class="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
              <p class="text-xs uppercase tracking-[0.2em] text-emerald-700">Estado actual</p>
              <p class="mt-2 text-xl font-semibold text-emerald-900">{{ formatStatus(tracking.estado) }}</p>
            </article>

            <article class="rounded-2xl border border-slate-200 bg-slate-50/70 p-5">
              <p class="text-xs uppercase tracking-[0.2em] text-slate-500">Cliente</p>
              <p class="mt-2 text-xl font-semibold text-slate-800">{{ tracking.cliente?.nombres || 'Cliente' }}</p>
            </article>
          </div>

          <div class="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
            <article class="rounded-2xl border border-slate-200 bg-white p-6">
              <h2 class="text-lg font-semibold text-slate-800">Detalle del ingreso</h2>
              <dl class="mt-5 grid gap-4 text-sm text-slate-700 md:grid-cols-2">
                <div>
                  <dt class="text-slate-500">Equipo</dt>
                  <dd class="mt-1 font-medium text-slate-900">{{ buildDeviceLabel() }}</dd>
                </div>
                <div>
                  <dt class="text-slate-500">Color</dt>
                  <dd class="mt-1 font-medium text-slate-900">{{ tracking.color || 'No especificado' }}</dd>
                </div>
                <div>
                  <dt class="text-slate-500">IMEI</dt>
                  <dd class="mt-1 font-medium text-slate-900">{{ tracking.imei || 'No registrado' }}</dd>
                </div>
                <div>
                  <dt class="text-slate-500">Serial</dt>
                  <dd class="mt-1 font-medium text-slate-900">{{ tracking.serial || 'No registrado' }}</dd>
                </div>
                <div>
                  <dt class="text-slate-500">Fecha de recepción</dt>
                  <dd class="mt-1 font-medium text-slate-900">{{ formatDate(tracking.fecha_recepcion) }}</dd>
                </div>
                <div>
                  <dt class="text-slate-500">Última actualización</dt>
                  <dd class="mt-1 font-medium text-slate-900">{{ formatDate(tracking.fecha_actualizacion) }}</dd>
                </div>
              </dl>
            </article>

            <article class="rounded-2xl border border-slate-200 bg-white p-6">
              <div class="flex items-start justify-between gap-3">
                <div>
                  <h2 class="text-lg font-semibold text-slate-800">Pago y avance</h2>
                  <p class="mt-1 text-sm text-slate-500">Resumen actualizado del cobro de esta orden.</p>
                </div>
                <span class="rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em]"
                  :class="paymentBadgeClass(tracking.pago?.estado)">
                  {{ tracking.pago?.label || 'Sin datos de pago' }}
                </span>
              </div>
              <dl class="mt-5 grid gap-4 text-sm text-slate-700 md:grid-cols-2">
                <div>
                  <dt class="text-slate-500">Total abonado</dt>
                  <dd class="mt-1 font-medium text-slate-900">{{ formatMoney(tracking.pago?.monto_pagado_total) }}</dd>
                </div>
                <div>
                  <dt class="text-slate-500">Total estimado</dt>
                  <dd class="mt-1 font-medium text-slate-900">{{ formatMoney(tracking.pago?.total_estimado) }}</dd>
                </div>
                <div>
                  <dt class="text-slate-500">Saldo pendiente</dt>
                  <dd class="mt-1 font-medium text-slate-900">{{ formatMoney(tracking.pago?.saldo_pendiente) }}</dd>
                </div>
              </dl>
            </article>
          </div>

          <article v-if="tracking.detalles?.length" class="rounded-2xl border border-slate-200 bg-white p-6">
            <h2 class="text-lg font-semibold text-slate-800">Detalle del servicio</h2>
            <div class="mt-4 overflow-hidden rounded-lg border border-slate-200">
              <table class="w-full text-sm">
                <thead class="bg-slate-100 text-slate-600">
                  <tr>
                    <th class="px-3 py-2 text-left">Concepto</th>
                    <th class="px-3 py-2 text-left">Cant.</th>
                    <th class="px-3 py-2 text-left">Precio</th>
                    <th class="px-3 py-2 text-left">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="detail in tracking.detalles" :key="detail.id" class="border-t border-slate-100">
                    <td class="px-3 py-2">{{ detail.descripcion }}</td>
                    <td class="px-3 py-2">{{ detail.cantidad }}</td>
                    <td class="px-3 py-2">{{ formatMoney(detail.precio_unitario) }}</td>
                    <td class="px-3 py-2 font-semibold text-slate-700">{{ formatMoney(detail.subtotal) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </article>

          <article class="rounded-2xl border border-slate-200 bg-white p-6">
            <h2 class="text-lg font-semibold text-slate-800">Observaciones</h2>
            <p class="mt-4 whitespace-pre-line text-sm leading-6 text-slate-700">
              {{ tracking.observaciones || 'Todavía no se han registrado observaciones adicionales para esta orden.' }}
            </p>
          </article>

          <article class="rounded-2xl border border-slate-200 bg-white p-6">
            <h2 class="text-lg font-semibold text-slate-800">Problema reportado</h2>
            <p class="mt-4 whitespace-pre-line text-sm leading-6 text-slate-700">
              {{ tracking.problema_reportado }}
            </p>
          </article>

          <article v-if="tracking.fotos?.length" class="rounded-2xl border border-slate-200 bg-white p-6">
            <h2 class="text-lg font-semibold text-slate-800">Fotos del equipo</h2>
            <div class="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <a v-for="photo in tracking.fotos" :key="photo.id" :href="photo.foto" target="_blank"
                rel="noopener noreferrer"
                class="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 hover:border-blue-300">
                <img :src="photo.foto" :alt="photo.nombre_archivo || 'Foto del equipo'"
                  class="h-44 w-full object-cover" />
                <div class="px-3 py-2 text-xs text-slate-500">{{ photo.nombre_archivo || 'Foto del equipo' }}</div>
              </a>
            </div>
          </article>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import api from "@/api";

export default {
  name: "WorkshopPublicTracking",
  data() {
    return {
      company: {
        brandName: "",
        phone: "",
      },
      loading: true,
      refreshing: false,
      lastLoadedAt: null,
      errorMessage: "",
      tracking: null,
    };
  },
  mounted() {
    this.loadCompany();
    this.loadTracking();
  },
  methods: {
    async loadCompany() {
      try {
        const { data } = await api.get("empresa");
        const company = Array.isArray(data) ? data[0] || {} : {};
        this.company = {
          brandName: company.nombre_comercial || company.razon_social || "Seguimiento",
          phone: company.telefonos || "",
        };
        document.title = `${this.company.brandName} | Seguimiento`;
      } catch (error) {
        document.title = "Seguimiento de servicio técnico";
      }
    },
    async loadTracking({ silent = false } = {}) {
      if (!silent) {
        this.loading = true;
      } else {
        this.refreshing = true;
      }

      if (!silent || !this.tracking) {
        this.errorMessage = "";
      }

      try {
        const slug = this.$route.params.slug;
        const { data } = await api.get(`taller/publico/${slug}`);
        this.tracking = data?.data || null;
        this.lastLoadedAt = new Date();

        if (!this.tracking) {
          this.errorMessage = "No se encontró información pública para este equipo.";
        }
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.error ||
          "La consulta pública no está disponible o ya fue cerrada.";
      } finally {
        if (!silent) {
          this.loading = false;
        } else {
          this.refreshing = false;
        }
      }
    },
    formatStatus(value) {
      return String(value || "Sin estado")
        .replaceAll("_", " ")
        .toLowerCase()
        .replace(/(^|\s)\S/g, (letter) => letter.toUpperCase());
    },
    formatDate(value) {
      if (!value) {
        return "No disponible";
      }

      return new Intl.DateTimeFormat("es-DO", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value));
    },
    formatMoney(value) {
      const amount = Number(value || 0);

      return new Intl.NumberFormat("es-DO", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    },
    buildDeviceLabel() {
      return [this.tracking?.tipo_equipo, this.tracking?.marca, this.tracking?.modelo]
        .filter(Boolean)
        .join(" - ") || "Equipo no especificado";
    },
    paymentBadgeClass(status) {
      if (status === "PAGADO") {
        return "bg-emerald-100 text-emerald-700";
      }

      if (status === "AVANCE") {
        return "bg-amber-100 text-amber-700";
      }

      return "bg-slate-100 text-slate-700";
    },
  },
};
</script>